import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { fetchVisionInspection } from '../../../services/api';
import { useTranslation } from '../../../context/LanguageContext';
import SyncSourceNote from './SyncSourceNote';
import { SHOW_DATA_SOURCE } from '../../../config/features';
import './VisionInspectionNew.css';
import './ShotSyncNew.css';

/* ===== 품질·생산 > 비전검사 =====
 * 예전「검수 사진」서브탭을 대체한다. 샷마다 연결된 비전 이미지와 비전 판정(OK/NG)·불량 유형을 보여준다.
 * 공정검사(공정 AI 판정)와는 별개 결과다. 같은 샷의 공정검사 결과는 상세 창에 참고로만 보여준다.
 *
 * 데이터: /machine/vision-inspection/{id}?date=&verdict=&defectType=&page=&size=
 *   - summary 는 그날 전체 기준(필터 무관), items 는 필터·페이지 적용.
 *   - 오늘 날짜를 보고 있으면 30초마다 새로 읽어 실시간으로 갱신한다.
 * 스타일은 vi- 접두어만 쓴다(Machine.css·다른 서브탭과 충돌 방지). */

const PAGE_SIZE = 8;                 // 4열 × 2행 (화면 맞춤: 카드가 남은 높이를 2행으로 나눠 채움)
const REFRESH_MS = 30000;            // 오늘 날짜일 때 자동 갱신 주기
const DEFECT_TYPES = ['FLASH', 'WELDLINE', 'SHORT', 'SINK'];
const UNKNOWN = 'UNKNOWN';

/* 비전 이미지 캐시 버전. nginx 가 .jpg 에 1년 immutable 캐시를 주므로 같은 파일명으로 이미지를
   교체하면 브라우저가 옛 그림을 계속 보여준다. 이미지셋을 바꾸면 이 숫자를 올릴 것. */
const VISION_IMAGE_VERSION = 2;

const pad2 = (n) => String(n).padStart(2, '0');
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

/* 'YYYY-MM-DDTHH:mm:ss[.SSS]' (KST, 시간대 없음) → 'HH:mm:ss'. Date 로 바꾸지 않고 문자열에서 자른다. */
const timeOf = (iso) => {
  if (!iso) return '-';
  const m = String(iso).match(/T(\d{2}:\d{2}:\d{2})/);
  return m ? m[1] : String(iso);
};
const dateTimeOf = (iso) => (iso ? String(iso).replace('T', ' ').slice(0, 19) : '-');

const imageSrc = (file) => (file ? `/vision/${file}?v=${VISION_IMAGE_VERSION}` : null);

/* 페이지 번호 목록. 앞쪽 번호를 쭉 보여주고 마지막 앞에 … 을 둔다. */
const buildPageItems = (page, totalPages) => {
  if (totalPages <= 9) return Array.from({ length: totalPages }, (_, i) => i);
  const items = [];
  const windowSize = 7;
  const start = Math.max(0, Math.min(page - 3, totalPages - windowSize - 1));
  if (start > 0) {
    items.push(0);
    if (start > 1) items.push('…');
  }
  for (let i = start; i < start + windowSize; i++) items.push(i);
  items.push('…', totalPages - 1);
  return items;
};

function VisionInspectionNew({ equipmentId }) {
  const { t } = useTranslation();
  const tr = useCallback((key, params) => t(`pages.monitoring.machine.visionInspection.${key}`, params), [t]);
  const typeLabel = useCallback((type) => tr(`types.${type || UNKNOWN}`), [tr]);

  const [date, setDate] = useState(todayStr());
  const [verdict, setVerdict] = useState('');        // '' | 'OK' | 'NG'
  const [defectType, setDefectType] = useState('');  // '' | FLASH | ...
  const [page, setPage] = useState(0);
  const verdictByType = useRef(false);               // 판정 '불량'이 유형 선택 때문에 자동으로 바뀐 것인지

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);    // 상세 창에 띄운 항목

  /* 설비가 바뀌면 처음 상태로 */
  useEffect(() => {
    setDate(todayStr());
    setVerdict('');
    setDefectType('');
    setPage(0);
    setSelected(null);
    verdictByType.current = false;
  }, [equipmentId]);

  const load = useCallback(async (silent = false) => {
    if (!equipmentId) return;
    if (!silent) setIsLoading(true);
    try {
      const res = await fetchVisionInspection(equipmentId, { date, verdict, defectType, page, size: PAGE_SIZE });
      setData(res || null);
      setError(null);
    } catch (e) {
      console.error('Failed to load vision inspection:', e);
      if (!silent) setError(t('pages.monitoring.machine.common.dataLoadFailed'));
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [equipmentId, date, verdict, defectType, page, t]);

  useEffect(() => { load(false); }, [load]);

  /* 오늘을 보고 있으면 주기적으로 조용히 갱신 */
  useEffect(() => {
    if (date !== todayStr()) return undefined;
    const id = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(id);
  }, [date, load]);

  /* ESC 로 상세 창 닫기 */
  useEffect(() => {
    if (!selected) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setSelected(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected]);

  const summary = data?.summary;
  const items = data?.items || [];
  const totalItems = data?.totalItems || 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));

  /* 날짜 옵션: 서버가 준 날짜 + 오늘(샷이 아직 없어도 고를 수 있게) */
  const dateOptions = useMemo(() => {
    const set = new Set(data?.availableDates || []);
    set.add(todayStr());
    set.add(date);
    return Array.from(set).sort((a, b) => (a < b ? 1 : -1));
  }, [data, date]);

  /* 유형 분포: 4종 + 기타(있을 때만) */
  const typeRows = useMemo(() => {
    const byType = summary?.byType || {};
    const rows = DEFECT_TYPES.map((type) => ({ type, count: Number(byType[type]) || 0 }));
    const unknown = Number(byType[UNKNOWN]) || 0;
    if (unknown > 0) rows.push({ type: UNKNOWN, count: unknown });
    return rows;
  }, [summary]);
  const typeMax = Math.max(1, ...typeRows.map((r) => r.count));
  const ngTotal = summary?.ngCount || 0;

  const changeDate = (e) => { setDate(e.target.value); setPage(0); };
  const changeVerdict = (v) => {
    setVerdict(v);
    verdictByType.current = false;          // 사용자가 직접 고른 판정
    if (v === 'OK') setDefectType('');      // 양품에는 유형이 없다
    setPage(0);
  };
  /* type 이 '' 이면 유형 해제. 같은 유형을 다시 눌러도 해제 */
  const changeType = (type) => {
    const next = type && defectType !== type ? type : '';
    setDefectType(next);
    if (next) {
      // 유형을 고르면 불량만 보는 것과 같다. 판정이 '전체'였을 때만 자동으로 '불량'으로 바꾸고 표시해 둔다
      if (verdict !== 'NG') { setVerdict('NG'); verdictByType.current = true; }
    } else if (verdictByType.current) {
      // 유형 때문에 자동으로 바뀐 '불량'이면, 유형을 해제할 때 판정도 '전체'로 되돌린다
      setVerdict('');
      verdictByType.current = false;
    }
    setPage(0);
  };

  const kpis = [
    { key: 'inspected', value: summary?.inspected ?? 0, tone: 'neutral' },
    { key: 'ok', value: summary?.okCount ?? 0, tone: 'good' },
    { key: 'ng', value: summary?.ngCount ?? 0, tone: 'bad' },
    { key: 'ngRate', value: summary?.ngRate != null ? `${Number(summary.ngRate).toFixed(2)}%` : '—', tone: 'bad' },
    { key: 'uninspected', value: summary?.uninspected ?? 0, tone: 'muted' }
  ];

  return (
    <div className="vi-root">
      {SHOW_DATA_SOURCE && <SyncSourceNote equipmentId={equipmentId} />}

      {/* 요약 */}
      <div className="vi-top">
        <div className="vi-kpis">
          {kpis.map((k) => (
            <div key={k.key} className={`vi-kpi vi-kpi-${k.tone}`}>
              <span className="vi-kpi-label">{tr(`kpi.${k.key}`)}</span>
              <span className="vi-kpi-value">{typeof k.value === 'number' ? k.value.toLocaleString() : k.value}</span>
            </div>
          ))}
        </div>

        <div className="vi-types">
          <div className="vi-types-head">
            <span className="vi-panel-title">{tr('typeDistribution')}</span>
            <span className="vi-types-sub">{tr('ngTotal', { n: ngTotal.toLocaleString() })}</span>
          </div>
          {typeRows.map((r) => (
            <button
              key={r.type}
              type="button"
              className={`vi-type-row ${defectType === r.type ? 'active' : ''}`}
              onClick={() => changeType(r.type)}
              title={tr('typeFilterHint')}
            >
              <span className={`vi-type-dot vi-type-${r.type}`} />
              <span className="vi-type-name">{typeLabel(r.type)}</span>
              <span className="vi-type-bar">
                <span className={`vi-type-fill vi-type-${r.type}`} style={{ width: `${(r.count / typeMax) * 100}%` }} />
              </span>
              <span className="vi-type-count">{r.count.toLocaleString()}</span>
              <span className="vi-type-pct">{ngTotal > 0 ? `${((r.count / ngTotal) * 100).toFixed(0)}%` : '—'}</span>
            </button>
          ))}
        </div>
      </div>

      {/* 필터 */}
      <div className="vi-filter-row">
        <span className="vi-filter-label">{tr('date')}</span>
        <div className="vi-select-wrap">
          <select className="vi-select" value={date} onChange={changeDate}>
            {dateOptions.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <span className="material-symbols-outlined vi-select-icon">expand_more</span>
        </div>

        <span className="vi-filter-label">{tr('verdict')}</span>
        <div className="vi-seg">
          {[['', 'all'], ['OK', 'ok'], ['NG', 'ng']].map(([v, key]) => (
            <button key={key} type="button" className={`vi-seg-btn ${verdict === v ? 'active' : ''}`} onClick={() => changeVerdict(v)}>
              {tr(`verdicts.${key}`)}
            </button>
          ))}
        </div>

        <span className="vi-filter-label">{tr('defectType')}</span>
        <div className="vi-chips">
          <button type="button" className={`vi-chip ${defectType === '' ? 'active' : ''}`} onClick={() => changeType('')}>
            {tr('verdicts.all')}
          </button>
          {DEFECT_TYPES.map((type) => (
            <button key={type} type="button" className={`vi-chip ${defectType === type ? 'active' : ''}`} onClick={() => changeType(type)}>
              <span className={`vi-type-dot vi-type-${type}`} />{typeLabel(type)}
            </button>
          ))}
        </div>

        <span className="vi-result-count">{tr('resultCount', { n: totalItems.toLocaleString() })}</span>
      </div>

      {/* 목록 */}
      {isLoading && !data ? (
        <div className="vi-message">{t('pages.monitoring.machine.common.loading')}</div>
      ) : error ? (
        <div className="vi-message vi-message-error">{error}</div>
      ) : items.length === 0 ? (
        <div className="vi-message">{summary?.inspected ? tr('emptyFiltered') : tr('empty')}</div>
      ) : (
        <div className="vi-grid">
          {items.map((it) => {
            const ng = it.verdict === 'NG';
            const src = imageSrc(it.imageFile);
            return (
              <button
                key={it.shotHistoryId}
                type="button"
                className={`vi-card ${ng ? 'vi-card-ng' : ''}`}
                onClick={() => setSelected(it)}
              >
                <div className="vi-card-img">
                  {src
                    ? <img src={src} alt={`Shot #${it.shotCount}`} loading="lazy" />
                    : <span className="vi-card-noimg">{tr('noImage')}</span>}
                  {ng && <span className={`vi-type-tag vi-type-${it.defectType || UNKNOWN}`}>{typeLabel(it.defectType)}</span>}
                </div>
                <div className="vi-card-footer">
                  <span className={`vi-badge ${ng ? 'vi-badge-ng' : 'vi-badge-ok'}`}>{tr(ng ? 'verdicts.ng' : 'verdicts.ok')}</span>
                  <span className="vi-card-title">Shot #{it.shotCount ?? '-'}</span>
                  <span className="vi-card-time">{timeOf(it.shotTime)}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* 페이지 */}
      {totalItems > PAGE_SIZE && (
        <div className="vi-pagination">
          <button type="button" className="vi-page-nav" disabled={page === 0} onClick={() => setPage(page - 1)}>
            <span className="material-symbols-outlined">chevron_left</span>{tr('prev')}
          </button>
          {buildPageItems(page, totalPages).map((item, idx) => (
            item === '…'
              ? <span key={`gap-${idx}`} className="vi-page-gap">···</span>
              : (
                <button key={item} type="button" className={`vi-page-num ${item === page ? 'active' : ''}`} onClick={() => setPage(item)}>
                  {item + 1}
                </button>
              )
          ))}
          <button type="button" className="vi-page-nav" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>
            {tr('next')}<span className="material-symbols-outlined">chevron_right</span>
          </button>
        </div>
      )}

      {/* 상세 창 */}
      {selected && (
        <div className="vi-modal-backdrop" onClick={() => setSelected(null)} role="presentation">
          <div className="vi-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="vi-modal-head">
              <span className="vi-modal-title">{tr('detailTitle', { n: selected.shotCount ?? '-' })}</span>
              <button type="button" className="vi-modal-close" onClick={() => setSelected(null)} aria-label={tr('close')}>
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="vi-modal-body">
              <div className="vi-modal-img">
                {imageSrc(selected.imageFile)
                  ? <img src={imageSrc(selected.imageFile)} alt={`Shot #${selected.shotCount}`} />
                  : <span className="vi-card-noimg">{tr('noImage')}</span>}
              </div>
              <dl className="vi-modal-info">
                <dt>{tr('field.verdict')}</dt>
                <dd>
                  <span className={`vi-badge ${selected.verdict === 'NG' ? 'vi-badge-ng' : 'vi-badge-ok'}`}>
                    {tr(selected.verdict === 'NG' ? 'verdicts.ng' : 'verdicts.ok')}
                  </span>
                  {SHOW_DATA_SOURCE && selected.verdictSource === 'DEMO' && <span className="ss-demo-tag">{t('pages.monitoring.machine.shotSync.demoTag')}</span>}
                </dd>
                <dt>{tr('field.defectType')}</dt>
                <dd>
                  {selected.verdict === 'NG'
                    ? <><span className={`vi-type-dot vi-type-${selected.defectType || UNKNOWN}`} />{typeLabel(selected.defectType)}</>
                    : '—'}
                </dd>
                <dt>{tr('field.shotTime')}</dt><dd>{dateTimeOf(selected.shotTime)}</dd>
                <dt>{tr('field.mold')}</dt><dd>{selected.craftId || '-'}</dd>
                <dt>{tr('field.processResult')}</dt>
                <dd>
                  {selected.processStatus === 'GOOD' && <span className="vi-badge vi-badge-ok">{tr('verdicts.ok')}</span>}
                  {(selected.processStatus === 'DEFECT' || selected.processStatus === 'BAD') && <span className="vi-badge vi-badge-ng">{tr('verdicts.ng')}</span>}
                  {!['GOOD', 'DEFECT', 'BAD'].includes(selected.processStatus) && <span className="vi-muted">{tr('unjudged')}</span>}
                </dd>
                {selected.syncStatus && (
                  <>
                    <dt>{tr('field.sync')}</dt>
                    <dd className="ss-inline">
                      <span className={`ss-badge ss-badge-${selected.syncStatus === 'MATCH' ? 'match' : 'check'}`}>
                        {t(`pages.monitoring.machine.shotSync.statuses.${selected.syncStatus}`)}
                      </span>
                      {selected.syncConfidence != null && <span>{Number(selected.syncConfidence).toFixed(2)}</span>}
                      {selected.syncResidualS != null && (
                        <span className="vi-muted">{t('pages.monitoring.machine.shotSync.unit.sec', { v: `${Number(selected.syncResidualS) > 0 ? '+' : ''}${Number(selected.syncResidualS).toFixed(1)}` })}</span>
                      )}
                    </dd>
                  </>
                )}
                <dt>{tr('field.imageFile')}</dt><dd className="vi-mono">{selected.imageFile || '-'}</dd>
                <dt>{tr('field.shotUuid')}</dt><dd className="vi-mono vi-small">{selected.shotUuid || '-'}</dd>
              </dl>
            </div>
            <div className="vi-modal-note">{tr('processNote')}</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VisionInspectionNew;
